import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BookingCoordination = () => {
  const [selectedBooking, setSelectedBooking] = useState(null);

  const activeBookings = [
    {
      id: 'BK-2024-001',
      partner: 'Global Airport Services',
      service: 'Airport Pickup',
      traveler: 'John Smith',
      location: 'JFK Airport, New York',
      date: '2024-01-15',
      time: '14:30',
      status: 'confirmed',
      cost: '$85',
      priority: 'high'
    },
    {
      id: 'BK-2024-002',
      partner: 'SecureTravel Protection',
      service: 'Executive Protection',
      traveler: 'Sarah Johnson',
      location: 'Dubai, UAE',
      date: '2024-01-16',
      time: '09:00',
      status: 'pending',
      cost: '$450',
      priority: 'urgent'
    },
    {
      id: 'BK-2024-003',
      partner: 'Elite Dining Network',
      service: 'Corporate Dinner',
      traveler: 'Michael Chen',
      location: 'Paris, France',
      date: '2024-01-17',
      time: '19:00',
      status: 'confirmed',
      cost: '$280',
      priority: 'normal'
    },
    {
      id: 'BK-2024-004',
      partner: 'MediCare International',
      service: 'Medical Consultation',
      traveler: 'Emily Rodriguez',
      location: 'London, UK',
      date: '2024-01-18',
      time: '11:00',
      status: 'in-progress',
      cost: '$150',
      priority: 'high'
    }
  ];

  const availableServices = [
    {
      partner: 'Global Airport Services',
      service: 'VIP Transfer',
      availability: 'Available Now',
      rate: '$120',
      rating: 4.8,
      responseTime: '15 min'
    },
    {
      partner: 'Premium Airport Concierge',
      service: 'Meet & Greet',
      availability: 'Available Now',
      rate: '$45',
      rating: 4.5,
      responseTime: '20 min'
    },
    {
      partner: 'SecureTravel Protection',
      service: 'Risk Assessment',
      availability: 'Next Day',
      rate: '$300',
      rating: 4.9,
      responseTime: '5 min'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'text-success bg-success/10 border-success/20';
      case 'pending': return 'text-warning bg-warning/10 border-warning/20';
      case 'in-progress': return 'text-primary bg-primary/10 border-primary/20';
      case 'cancelled': return 'text-error bg-error/10 border-error/20';
      default: return 'text-muted-foreground bg-muted/10 border-muted/20';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'text-error';
      case 'high': return 'text-warning';
      case 'normal': return 'text-primary';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="space-y-6">
      {/* Booking Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="glass-container rounded-xl border border-border/50 p-6">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Calendar" size={24} className="text-primary" />
            <span className="text-2xl font-bold text-foreground">{activeBookings?.length}</span>
          </div>
          <p className="text-sm text-muted-foreground">Active Bookings</p>
        </div>
        <div className="glass-container rounded-xl border border-border/50 p-6">
          <div className="flex items-center justify-between mb-2">
            <Icon name="CheckCircle" size={24} className="text-success" />
            <span className="text-2xl font-bold text-foreground">{activeBookings?.filter(b => b?.status === 'confirmed')?.length}</span>
          </div>
          <p className="text-sm text-muted-foreground">Confirmed</p>
        </div>
        <div className="glass-container rounded-xl border border-border/50 p-6">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Clock" size={24} className="text-warning" />
            <span className="text-2xl font-bold text-foreground">{activeBookings?.filter(b => b?.status === 'pending')?.length}</span>
          </div>
          <p className="text-sm text-muted-foreground">Pending</p>
        </div>
        <div className="glass-container rounded-xl border border-border/50 p-6">
          <div className="flex items-center justify-between mb-2">
            <Icon name="DollarSign" size={24} className="text-accent" />
            <span className="text-2xl font-bold text-foreground">$965</span>
          </div>
          <p className="text-sm text-muted-foreground">Total Cost</p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Active Bookings */}
        <div className="lg:col-span-2 glass-container rounded-xl border border-border/50">
          <div className="p-6 border-b border-border/50">
            <h3 className="text-lg font-semibold text-foreground">Active Bookings</h3>
            <p className="text-sm text-muted-foreground mt-1">Manage current and upcoming bookings</p>
          </div>
          <div className="p-6 space-y-4">
            {activeBookings?.map((booking) => (
              <div
                key={booking?.id}
                className="p-4 rounded-lg border border-border/30 hover:bg-white/5 transition-all duration-200"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-sm font-mono text-muted-foreground">{booking?.id}</span>
                      <div className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(booking?.status)}`}>
                        {booking?.status}
                      </div>
                      <Icon name="Flag" size={14} className={getPriorityColor(booking?.priority)} />
                    </div>
                    <h4 className="font-semibold text-foreground mb-1">{booking?.service}</h4>
                    <p className="text-sm text-muted-foreground">{booking?.partner}</p>
                  </div>
                  <span className="text-lg font-bold text-foreground">{booking?.cost}</span>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="flex items-center space-x-2">
                    <Icon name="User" size={14} className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">{booking?.traveler}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="MapPin" size={14} className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">{booking?.location}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Calendar" size={14} className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">{booking?.date}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Clock" size={14} className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">{booking?.time}</span>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Button variant="outline" size="xs" iconName="Eye">
                    Details
                  </Button>
                  <Button variant="ghost" size="xs" iconName="MessageSquare">
                    Contact
                  </Button>
                  {booking?.status === 'pending' && (
                    <Button variant="success" size="xs" iconName="Check">
                      Approve
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Available Services */}
        <div className="glass-container rounded-xl border border-border/50">
          <div className="p-6 border-b border-border/50">
            <h3 className="text-lg font-semibold text-foreground">Available Services</h3>
            <p className="text-sm text-muted-foreground mt-1">Quick booking options</p>
          </div>
          <div className="p-6 space-y-4">
            {availableServices?.map((service, idx) => (
              <div
                key={idx}
                className="p-4 rounded-lg border border-border/30 hover:bg-white/5 transition-all duration-200"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">{service?.service}</h4>
                    <p className="text-sm text-muted-foreground">{service?.partner}</p>
                  </div>
                  <span className="text-lg font-bold text-primary">{service?.rate}</span>
                </div>

                <div className="space-y-2 mb-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Availability</span>
                    <span className="text-xs font-medium text-success">{service?.availability}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Rating</span>
                    <div className="flex items-center space-x-1">
                      <Icon name="Star" size={12} className="text-warning" />
                      <span className="text-xs font-medium text-foreground">{service?.rating}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Response</span>
                    <span className="text-xs font-medium text-foreground">{service?.responseTime}</span>
                  </div>
                </div>

                <Button variant="default" size="sm" iconName="Plus" className="w-full">
                  Book Now
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingCoordination;